import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "errors", "avatarPreview", "avatarInput", "submitBtn"]
  static values = { url: String }

  connect() {
    this._originalBtnText = this.submitBtnTarget.textContent
  }

  submit(event) {
    if (event) event.preventDefault()

    const formData = new FormData(this.formTarget)

    // Only include avatar in the request when a new file was selected
    const avatarFile = this.avatarInputTarget.files[0]
    if (avatarFile) {
      formData.append("user[avatar]", avatarFile)
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = this.submitBtnTarget.dataset.savingText || "Saving..."

    fetch(this.urlValue, {
      method: "PUT",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: formData
    })
    .then(response => response.json().then(data => ({ ok: response.ok, data })))
    .then(({ ok, data }) => {
      if (ok && data.success) {
        this._updateUI(data)
        this._showFlash("notice", data.message)
        this._closeModal()

        // Reload if locale changed so the entire UI re-renders in the new language
        if (data.reload || (data.locale && data.locale !== document.documentElement.lang)) {
          window.location.reload()
        }
      } else {
        this._showErrors(data.errors || ["Update failed"])
      }
    })
    .catch(() => {
      this._showErrors(["A network error occurred. Please try again."])
    })
    .finally(() => {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = this._originalBtnText
    })
  }

  previewAvatar() {
    const file = this.avatarInputTarget.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      this._previewDataUrl = e.target.result

      const preview = this.avatarPreviewTarget
      if (preview.tagName === "IMG") {
        preview.src = e.target.result
      } else {
        // Replace initials span with an img
        const img = document.createElement("img")
        img.src = e.target.result
        img.alt = "Avatar"
        img.className = "w-16 h-16 rounded-full object-cover"
        img.dataset.profileFormTarget = "avatarPreview"
        preview.replaceWith(img)
      }
    }
    reader.readAsDataURL(file)
  }

  _updateUI(data) {
    // Update all name elements across sidebar, mobile menu, and header
    if (data.full_name) {
      document.querySelectorAll("[data-user-name]").forEach(el => {
        el.textContent = data.full_name
      })
    }

    // Use the server avatar URL if available, otherwise fall back to the
    // client-side preview (data URL) when background processing is pending.
    const avatarSrc = data.avatar_url || this._previewDataUrl

    if (avatarSrc) {
      document.querySelectorAll("[data-user-avatar]").forEach(el => {
        el.src = avatarSrc
      })
      document.querySelectorAll("[data-user-initials]").forEach(el => {
        const img = document.createElement("img")
        img.src = avatarSrc
        img.alt = data.full_name || "Avatar"
        img.dataset.userAvatar = true
        // Carry over sizing classes from the span
        img.className = el.className.replace(/bg-brand|text-white|flex|items-center|justify-center|text-\w+|font-semibold/g, "").trim() + " object-cover"
        el.replaceWith(img)
      })
    }
  }

  _showErrors(messages) {
    this.errorsTarget.textContent = ""
    messages.forEach(m => {
      const p = document.createElement("p")
      p.textContent = m
      this.errorsTarget.appendChild(p)
    })
    this.errorsTarget.classList.remove("hidden")
  }

  _closeModal() {
    this.errorsTarget.classList.add("hidden")
    this.dispatch("close", { bubbles: true })
  }

  _showFlash(type, message) {
    const cssType = type === "notice" ? "notice" : "alert"
    const container = document.createElement("div")
    container.className = `flash-toast-${cssType}`
    container.dataset.controller = "flash"
    container.dataset.flashDismissAfterValue = "5000"
    container.setAttribute("role", "alert")

    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`

    const msgEl = document.createElement("p")
    msgEl.className = "flex-1 text-sm"
    msgEl.textContent = message

    container.appendChild(iconDiv)
    container.appendChild(msgEl)
    document.body.insertBefore(container, document.body.firstChild)
  }
}
